import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { clearEngineSession, engineApiClient } from '../../shared/api/engineApiClient';
import { MAIN_BASE_URL } from '../../shared/config/environment';
import { createHandoffRequest, safeReturnPath } from './pkce';

type SessionStatus = 'checking' | 'unauthenticated' | 'authenticated' | 'error';

interface EngineSessionContextValue {
  status: SessionStatus;
  error?: string;
  startSignIn: (returnPath?: string) => Promise<void>;
  exchangeHandoff: (code: string, verifier: string) => Promise<string>;
  disconnect: () => Promise<void>;
}

const EngineSessionContext = createContext<EngineSessionContextValue | null>(null);

export const EngineSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<SessionStatus>('checking');
  const [error, setError] = useState<string>();

  useEffect(() => {
    // The callback owns the one-time exchange. Running refresh in parallel can
    // race an empty pre-exchange cookie check against the newly created session.
    if (window.location.pathname === '/auth/callback') return;
    let active = true;
    void engineApiClient.restoreSession().then((session) => {
      if (active) setStatus(session ? 'authenticated' : 'unauthenticated');
    }).catch(() => {
      if (active) {
        clearEngineSession();
        setStatus('unauthenticated');
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const startSignIn = useCallback(async (returnPath = '/documents') => {
    setError(undefined);
    const request = await createHandoffRequest(safeReturnPath(returnPath));
    const query = new URLSearchParams({
      target: 'pm',
      return_path: request.returnPath,
      state: request.state,
      code_challenge: request.challenge,
    });
    window.location.assign(`${MAIN_BASE_URL}/auth/engine/continue?${query}`);
  }, []);

  const exchangeHandoff = useCallback(async (code: string, verifier: string) => {
    try {
      setError(undefined);
      const result = await engineApiClient.exchangeHandoff(code, verifier);
      setStatus('authenticated');
      return safeReturnPath(result.returnPath);
    } catch (exchangeError) {
      clearEngineSession();
      setStatus('error');
      const message = exchangeError instanceof Error
        ? exchangeError.message
        : '账号连接失败，请重新尝试';
      setError(message);
      throw exchangeError;
    }
  }, []);

  const disconnect = useCallback(async () => {
    try {
      await engineApiClient.logout();
    } finally {
      clearEngineSession();
      setStatus('unauthenticated');
      setError(undefined);
    }
  }, []);

  const value = useMemo<EngineSessionContextValue>(() => ({
    status,
    error,
    startSignIn,
    exchangeHandoff,
    disconnect,
  }), [disconnect, error, exchangeHandoff, startSignIn, status]);

  return (
    <EngineSessionContext.Provider value={value}>
      {children}
    </EngineSessionContext.Provider>
  );
};

export function useEngineSession(): EngineSessionContextValue {
  const value = useContext(EngineSessionContext);
  if (!value) throw new Error('useEngineSession must be used inside EngineSessionProvider');
  return value;
}
